import {
  Alert,
  FormControl,
  InputLabel,
  MenuItem,
  Radio,
  Select,
  SelectChangeEvent,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material"
import { useTranslation } from "react-i18next"

import { BookingLength } from "@/queries/models"

type PassDurationProps = {
  bookingLengths: BookingLength[] | undefined
  isLoading: boolean
  isError: boolean
  selectedBookingLengthId: string
  onSelect: (bookingLength: BookingLength) => void
  onBookingLengthAnalytics?: (bookingLength: BookingLength) => void
  isTicketCategory?: boolean
}

const PassDurationSelector = ({
  bookingLengths,
  isLoading = false,
  isError = false,
  selectedBookingLengthId,
  onSelect,
  onBookingLengthAnalytics,
  isTicketCategory,
}: PassDurationProps) => {
  const { t } = useTranslation()
  const label = isTicketCategory
    ? t("selectActivityLabel")
    : t("bookingLengthLabel")
  const handleChange = (event: SelectChangeEvent) => {
    const bookingLengthId = event.target.value
    const bookingLength = bookingLengths?.find(
      (bl) => bl.id === bookingLengthId
    )
    if (bookingLength) {
      onBookingLengthAnalytics?.(bookingLength)
      onSelect(bookingLength)
    }
  }

  if (isLoading) {
    return (
      <Skeleton
        data-testid="passduration-skeleton"
        width={"100%"}
        height={56}
      />
    )
  }

  if (isError || !bookingLengths) {
    return (
      <Alert severity="error" data-testid="data-fetch-error">
        {t("bookingLengthsFetchError")}
      </Alert>
    )
  }

  if (bookingLengths.length === 0) {
    return null
  }

  if (bookingLengths.length === 1) {
    const bookingLength = bookingLengths[0]
    return (
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography
          variant="body2"
          id="pass-duration-select-label"
          fontWeight="bold"
        >
          {label}:
        </Typography>
        <Typography variant="body2" fontWeight="bold">
          {bookingLength.name}
        </Typography>
      </Stack>
    )
  }

  return (
    <FormControl fullWidth>
      <InputLabel id="pass-duration-select-label">{label}</InputLabel>
      <Select
        labelId="pass-duration-select-label"
        id="pass-duration-select"
        value={selectedBookingLengthId}
        label={label}
        onChange={handleChange}
        MenuProps={{ disableScrollLock: true }}
        renderValue={(value) =>
          bookingLengths?.find((bl) => bl.id === value)?.name
        }
      >
        {bookingLengths.map((bl) => (
          <MenuItem key={bl.id} value={bl.id}>
            <Radio
              checked={selectedBookingLengthId === bl.id}
              aria-hidden="true"
            />
            <Stack>{bl.name}</Stack>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}

export default PassDurationSelector
